import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api, formatTarih, formatTl, waLink, CARI_TIP_AD, CARI_DURUM_AD } from '../lib/api';
import { useAuth } from '../lib/auth';
import Ipucu from '../ui/Ipucu';
import { CariHatirlatma } from '../ui/HatirlatCekmece';
import CariKasa from '../ui/CariKasa';
import CariCek from '../ui/CariCek';

type Detay = {
  id: number; code: string | null; name: string; type: string; status: string;
  phone: string | null; email: string | null; city: string | null; address: string | null;
  segment: string | null; notes: string | null;
  sorumlu: { id: number; name: string } | null;
  sonTemas: string | null; ilkTemas: string | null;
  kisiler: { id: number; name: string; title: string | null; phone: string | null; email: string | null; isPrimary: boolean }[];
  aktiviteler: { id: number; type: string; subject: string; body: string | null; happenedAt: string; yazan: string | null; kisi: string | null }[];
  teklifler: { id: number; no: string; statusLabel: string; totalKurus: number; createdAt: string }[];
  siparisler: { id: number; no: string; title: string; statusLabel: string; dueDate: string | null }[];
  numuneler: { id: number; no: string; description: string; status: string; sentAt: string | null; feedback: string | null }[];
  hatirlatmalar: { id: number; title: string; dueAt: string; sahip: string | null; gecikti: boolean }[];
  ekstre?: {
    resmi: { toplamKurus: number; adet: number };
    faturasiz?: { toplamKurus: number; adet: number };
    acikAlacakKurus: number; acikAlacakAdedi: number;
  };
};

const AKTIVITE_IKON: Record<string, string> = {
  ARAMA: '📞', ZIYARET: '🚗', MAIL: '✉️', WHATSAPP: '💬', NUMUNE: '◫', NOT: '✎',
};

/**
 * CARİ 360 (CRM eki 13.2).
 *
 * "Bu müşteriyle en son ne konuştuk, hangi numuneyi bekliyor, ne kadar borcu
 * var" — hepsi tek ekranda. EKSTRE resmî ve faturasız AYRI gösteriliyor;
 * birleştirilmiş tek rakam işletmenin gerçek durumunu gizliyor.
 */
export default function Cari360() {
  const { id } = useParams();
  const git = useNavigate();
  const { can } = useAuth();
  const [d, setD] = useState<Detay | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [aktiviteAcik, setAktiviteAcik] = useState(false);

  const yukle = useCallback(() => {
    api<Detay>(`/api/crm/cari/${id}`).then(setD).catch((e) => setHata((e as Error).message));
  }, [id]);
  useEffect(yukle, [yukle]);

  if (hata) {
    return (
      <div className="ccd-banner ccd-banner-danger">
        <span className="ccd-banner-icon">!</span><span>{hata}</span>
      </div>
    );
  }
  if (!d) return <div className="ccd-inline-note">Yükleniyor…</div>;

  const whatsapp = waLink(d.phone);

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="ccd-eyebrow">
            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => git('/crm')}>
              ← Cariler
            </button>
          </div>
          <h1 className="ccd-title">{d.name}</h1>
          <div className="ccd-meta">
            {d.code && <span className="ccd-mono">{d.code} · </span>}
            {CARI_TIP_AD[d.type] ?? d.type}{d.city ? ` · ${d.city}` : ''}{d.segment ? ` · segment ${d.segment}` : ''}
            {d.sorumlu ? ` · sorumlu: ${d.sorumlu.name}` : ''}
            {d.status !== 'AKTIF' && ` · ${CARI_DURUM_AD[d.status] ?? d.status}`}
          </div>
        </div>
        <div className="ccd-row" style={{ flexWrap: 'wrap' }}>
          {whatsapp && (
            <a className="ccd-btn ccd-btn-sm" href={whatsapp} target="_blank" rel="noreferrer">
              WhatsApp
            </a>
          )}
          {can('quote.write') && (
            <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => setAktiviteAcik(true)}>
              + Aktivite
            </button>
          )}
        </div>
      </div>

      {/* Ekstre — resmî ve faturasız AYRI */}
      {d.ekstre && (
        <div className="ccd-grid ccd-grid-3">
          <div className="ccd-stat">
            <div className="ccd-stat-label">Resmî ciro <Ipucu id="cari-resmi-ciro" /></div>
            <div className="ccd-stat-value">{formatTl(d.ekstre.resmi.toplamKurus)}</div>
            <div className="ccd-stat-foot">{d.ekstre.resmi.adet} fatura</div>
          </div>
          {d.ekstre.faturasiz && (
            <div className="ccd-stat">
              <div className="ccd-stat-label">Faturasız <Ipucu id="faturasiz-cikis" /></div>
              <div className="ccd-stat-value">{formatTl(d.ekstre.faturasiz.toplamKurus)}</div>
              <div className="ccd-stat-foot">{d.ekstre.faturasiz.adet} çıkış</div>
            </div>
          )}
          <div className="ccd-stat">
            <div className="ccd-stat-label">Açık alacak <Ipucu id="faturasiz-alacak" /></div>
            <div className="ccd-stat-value">
              <span className={d.ekstre.acikAlacakKurus > 0 ? 'ccd-kpi-down' : ''}>
                {formatTl(d.ekstre.acikAlacakKurus)}
              </span>
            </div>
            <div className="ccd-stat-foot">{d.ekstre.acikAlacakAdedi} kayıt</div>
          </div>
        </div>
      )}

      {d.hatirlatmalar.length > 0 && (
        <div className="ccd-banner ccd-banner-warning">
          <span className="ccd-banner-icon" aria-hidden="true">🔔</span>
          <div>
            <div style={{ fontWeight: 600 }}>{d.hatirlatmalar.length} açık hatırlatma</div>
            <div className="ccd-meta">
              {d.hatirlatmalar.map((h) => (
                <div key={h.id} className={h.gecikti ? 'ccd-kpi-down' : ''}>
                  {h.title} — {formatTarih(h.dueAt)}{h.sahip ? ` · ${h.sahip}` : ''}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="ccd-grid ccd-grid-2">
        {/* Kişiler + iletişim */}
        <div className="ccd-card ccd-stack-sm">
          <div className="ccd-row-between">
            <div className="ccd-fieldset-title">Kişiler</div>
            {can('quote.write') && <HizliEkle etiket="+ Kişi" alan="Ad soyad" ikinci="Telefon (isteğe bağlı)"
              gonder={(a, b) => api(`/api/crm/cari/${id}/kisi`, { method: 'POST', body: JSON.stringify({ name: a, phone: b || undefined }) }).then(yukle)} />}
          </div>
          {d.kisiler.length === 0 ? (
            <div className="ccd-meta">
              Kişi eklenmemiş. {d.phone && `Genel telefon: ${d.phone}`}
            </div>
          ) : d.kisiler.map((k) => (
            <div key={k.id} className="ccd-row-between">
              <div>
                <b>{k.name}</b>{k.isPrimary && <span className="ccd-badge ccd-badge-info"> birincil</span>}
                <div className="ccd-meta">
                  {k.title}{k.phone ? ` · ${k.phone}` : ''}{k.email ? ` · ${k.email}` : ''}
                </div>
              </div>
              {waLink(k.phone) && (
                <a className="ccd-btn ccd-btn-ghost ccd-btn-sm"
                   href={waLink(k.phone)!} target="_blank" rel="noreferrer">
                  WhatsApp
                </a>
              )}
            </div>
          ))}
          {d.address && <div className="ccd-meta">{d.address}</div>}
          {d.notes && <div className="ccd-meta">{d.notes}</div>}
        </div>

        {/* Numuneler */}
        <div className="ccd-card ccd-stack-sm">
          <div className="ccd-row-between">
            <div className="ccd-fieldset-title">Numuneler ({d.numuneler.length})</div>
            {can('quote.write') && <HizliEkle etiket="+ Numune" alan="Ne gönderilecek? (ürün, ebat, renk)"
              gonder={(a) => api('/api/crm/numune', { method: 'POST', body: JSON.stringify({ customerId: Number(id), description: a }) }).then(yukle)} />}
          </div>
          {d.numuneler.length === 0 ? (
            <div className="ccd-meta">Numune kaydı yok.</div>
          ) : d.numuneler.map((n) => (
            <div key={n.id} className="ccd-row-between">
              <div>
                <b className="ccd-mono">{n.no}</b> {n.description}
                {n.feedback && <div className="ccd-meta">“{n.feedback}”</div>}
              </div>
              <span className="ccd-badge">{NUMUNE_DURUM_AD[n.status] ?? n.status}</span>
            </div>
          ))}
        </div>
      </div>

      {d.ekstre && <NeAldi cariId={Number(id)} />}
      {d.ekstre && <Ekstre cariId={Number(id)} />}
      {/* Borç hatırlatma: geçmiş iletiler + "bu cariye hatırlatma gönderme" anahtarı. */}
      {can('reminder.view') && <CariHatirlatma cariId={Number(id)} />}
      {/* Kasa hareketleri ve emanet bakiyesi — yalnız kasayı görebilen rolde (API'de de `cash.view`). */}
      {can('cash.view') && <CariKasa cariId={Number(id)} />}
      {/* Çek ve senetleri, risk toplamı — yalnız çekleri görebilen rolde (API'de de `cheque.view`). */}
      {can('cheque.view') && <CariCek cariId={Number(id)} />}

      {/* Zincir: teklif → sipariş */}
      <div className="ccd-grid ccd-grid-2">
        <div className="ccd-card ccd-stack-sm">
          <div className="ccd-fieldset-title">Teklifler ({d.teklifler.length})</div>
          {d.teklifler.length === 0 ? <div className="ccd-meta">Teklif yok.</div>
            : d.teklifler.map((t) => (
              <div key={t.id} className="ccd-row-between">
                <Link to={`/teklif/${t.id}`} className="ccd-mono">{t.no}</Link>
                <span className="ccd-meta">{t.statusLabel} · {formatTl(t.totalKurus)}</span>
              </div>
            ))}
        </div>
        <div className="ccd-card ccd-stack-sm">
          <div className="ccd-fieldset-title">Siparişler ({d.siparisler.length})</div>
          {d.siparisler.length === 0 ? <div className="ccd-meta">Sipariş yok.</div>
            : d.siparisler.map((o) => (
              <div key={o.id} className="ccd-row-between">
                <div>
                  <span className="ccd-mono">{o.no}</span>
                  <div className="ccd-meta">{o.title}</div>
                </div>
                <span className="ccd-badge">{o.statusLabel}</span>
              </div>
            ))}
        </div>
      </div>

      {/* İletişim geçmişi */}
      <div className="ccd-card">
        <div className="ccd-fieldset-title">
          İletişim geçmişi
          {d.sonTemas && <span className="ccd-meta"> · son temas {formatTarih(d.sonTemas)}</span>}
        </div>
        {d.aktiviteler.length === 0 ? (
          <div className="ccd-meta">Henüz aktivite kaydı yok.</div>
        ) : (
          <div className="ccd-timeline">
            {d.aktiviteler.map((a) => (
              <div className="ccd-tl-item" key={a.id}>
                <span className="ccd-tl-dot" aria-hidden="true">{AKTIVITE_IKON[a.type] ?? '•'}</span>
                <div className="ccd-tl-body">
                  <div className="ccd-tl-head">
                    <span className="ccd-tl-text">{a.subject}</span>
                    <span className="ccd-tl-time">{formatTarih(a.happenedAt)}</span>
                  </div>
                  {a.body && <div className="ccd-meta">{a.body}</div>}
                  <div className="ccd-meta ccd-tl-actor">
                    {a.type.toLowerCase()}{a.kisi ? ` · ${a.kisi}` : ''}{a.yazan ? ` · ${a.yazan}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {aktiviteAcik && (
        <YeniAktivite customerId={d.id} kisiler={d.kisiler}
                      kapat={() => setAktiviteAcik(false)}
                      eklendi={() => { setAktiviteAcik(false); yukle(); }} />
      )}
    </>
  );
}

/* ─── Aktivite kaydı ───────────────────────────────────────────────────── */
/** Bu yıl bu cariye en çok ne satıldı (fatura satırlarından). */
function NeAldi({ cariId }: { cariId: number }) {
  const yil = new Date().getFullYear();
  const [u, setU] = useState<{ ad: string; birim: string | null; miktar: number; tutarKurus: number; faturaAdet: number }[] | null>(null);
  useEffect(() => {
    setU(null);
    api<{ urunler: NonNullable<typeof u> }>(`/api/finans/urunler?cari=${cariId}&yil=${yil}`).then((r) => setU(r.urunler)).catch(() => setU([]));
  }, [cariId, yil]);
  if (!u || u.length === 0) return null;
  return (
    <div className="ccd-card ccd-stack-sm">
      <div className="ccd-row-between">
        <div className="ccd-fieldset-title">{yil} — en çok aldığı ürünler</div>
        <div className="ccd-meta">Toplam {formatTl(u.reduce((s, x) => s + x.tutarKurus, 0))} · KDV hariç, ilk {u.length}</div>
      </div>
      {u.map((x, n) => (
        <div key={n} className="ccd-row-between">
          <span>{x.ad}</span>
          <span><b>{formatTl(x.tutarKurus)}</b> <span className="ccd-meta">· {x.miktar.toLocaleString('tr-TR')} {x.birim ?? ''} · {x.faturaAdet} fatura</span></span>
        </div>
      ))}
    </div>
  );
}

type EkstreVeri = {
  satirlar: { tarih: string; tur: string; no: string; aciklama: string | null; borcKurus: number; alacakKurus: number;
    kalanKurus: number | null; vade: string | null; durum: string | null; bakiyeKurus: number }[];
  toplam: { borcKurus: number; alacakKurus: number; bakiyeKurus: number };
  parasutBakiyeKurus: number | null;
};

/** Cari ekstresi: faturalar, iadeler, ödemeler ve (yetkiliyse) faturasız çıkışlar; yürüyen bakiye. */
function Ekstre({ cariId }: { cariId: number }) {
  const [v, setV] = useState<EkstreVeri | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [hepsi, setHepsi] = useState(false);
  useEffect(() => {
    setV(null);
    api<EkstreVeri>(`/api/finans/ekstre/${cariId}`).then(setV).catch((e) => setHata((e as Error).message));
  }, [cariId]);
  if (hata) return <div className="ccd-card ccd-meta">Ekstre açılamadı: {hata}</div>;
  if (!v) return <div className="ccd-card ccd-meta">Ekstre yükleniyor…</div>;
  const satirlar = hepsi ? v.satirlar : v.satirlar.slice(0, 12);
  const bakiyeYaz = (k: number) => k === 0 ? '₺0' : k > 0 ? `${formatTl(k)} borçlu` : `${formatTl(-k)} alacaklı`;
  return (
    <div className="ccd-card ccd-stack-sm">
      <div className="ccd-row-between" style={{ flexWrap: 'wrap' }}>
        <div className="ccd-fieldset-title">Cari ekstresi <Ipucu id="ekstre" /></div>
        <div className="ccd-meta">
          Borç {formatTl(v.toplam.borcKurus)} · Ödenen/iade {formatTl(v.toplam.alacakKurus)} · <b>Kalan {bakiyeYaz(v.toplam.bakiyeKurus)}</b>
          {v.parasutBakiyeKurus != null && <> · Paraşüt bakiyesi <b>{bakiyeYaz(v.parasutBakiyeKurus)}</b> <Ipucu id="cari-bakiye" /></>}
        </div>
      </div>
      {v.satirlar.length === 0 ? <div className="ccd-meta">Bu cariye ait fatura ya da çıkış yok.</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table className="ccd-table">
            <thead><tr className="ccd-tr">
              <th className="ccd-th">Tarih</th><th className="ccd-th">İşlem</th><th className="ccd-th">No</th>
              <th className="ccd-th ccd-td-num">Borç</th><th className="ccd-th ccd-td-num">Alacak</th>
              <th className="ccd-th ccd-td-num">Bakiye <Ipucu id="ekstre" /></th><th className="ccd-th">Vade</th><th className="ccd-th">Durum</th>
            </tr></thead>
            <tbody>
              {satirlar.map((s, n) => (
                <tr key={n} className="ccd-tr">
                  <td className="ccd-td">{formatTarih(s.tarih)}</td>
                  <td className="ccd-td">{s.tur}{s.aciklama && <div className="ccd-meta">{s.aciklama}</div>}</td>
                  <td className="ccd-td ccd-mono">{s.no}</td>
                  <td className="ccd-td ccd-td-num">{s.borcKurus ? formatTl(s.borcKurus) : ''}</td>
                  <td className="ccd-td ccd-td-num">{s.alacakKurus ? formatTl(s.alacakKurus) : ''}</td>
                  <td className="ccd-td ccd-td-num"><b>{formatTl(s.bakiyeKurus)}</b></td>
                  <td className="ccd-td">{s.vade ? formatTarih(s.vade) : ''}</td>
                  <td className="ccd-td">{s.durum && <span className="ccd-badge">{s.durum}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {v.satirlar.length > 12 && (
        <button className="ccd-btn ccd-btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setHepsi(!hepsi)}>
          {hepsi ? 'Son 12 hareketi göster' : `Tüm hareketler (${v.satirlar.length})`}
        </button>
      )}
      <div className="ccd-meta">Panelde eşitlenen faturalardan oluşur. Daha eski dönem ve faturaya bağlanmamış ödemeler için Paraşüt bakiyesi esastır.</div>
    </div>
  );
}

const NUMUNE_DURUM_AD: Record<string, string> = {
  HAZIRLANIYOR: 'Hazırlanıyor', GONDERILDI: 'Gönderildi', ONAY: 'Onaylandı', RET: 'Reddedildi', SIPARISE_DONDU: 'Siparişe döndü',
};

/** Kart başlığında tek satırlık hızlı ekleme: düğme → bir iki kutu → Kaydet. */
function HizliEkle({ etiket, alan, ikinci, gonder }: {
  etiket: string; alan: string; ikinci?: string;
  gonder: (a: string, b: string) => Promise<unknown>;
}) {
  const [acik, setAcik] = useState(false);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  if (!acik) return <button className="ccd-btn ccd-btn-sm" onClick={() => setAcik(true)}>{etiket}</button>;
  return (
    <form className="ccd-stack-sm" style={{ flex: 1, marginLeft: 12 }} onSubmit={async (e) => {
      e.preventDefault();
      if (a.trim().length < 2) { setHata(`${alan} gerekli`); return; }
      setBekliyor(true); setHata(null);
      try { await gonder(a.trim(), b.trim()); setA(''); setB(''); setAcik(false); }
      catch (err) { setHata(err instanceof Error ? err.message : 'Kaydedilemedi'); }
      finally { setBekliyor(false); }
    }}>
      <input className="ccd-input" autoFocus placeholder={alan} value={a} onChange={(e) => setA(e.target.value)} />
      {ikinci && <input className="ccd-input" placeholder={ikinci} value={b} onChange={(e) => setB(e.target.value)} />}
      {hata && <div className="ccd-meta" style={{ color: 'var(--ccd-danger)' }}>{hata}</div>}
      <div className="ccd-row" style={{ gap: 8 }}>
        <button className="ccd-btn ccd-btn-primary ccd-btn-sm" disabled={bekliyor}>Kaydet</button>
        <button type="button" className="ccd-btn ccd-btn-sm" onClick={() => { setAcik(false); setHata(null); }}>Vazgeç</button>
      </div>
    </form>
  );
}

function YeniAktivite({
  customerId, kisiler, kapat, eklendi,
}: {
  customerId: number;
  kisiler: { id: number; name: string }[];
  kapat: () => void; eklendi: () => void;
}) {
  const [type, setType] = useState('ARAMA');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [contactId, setContactId] = useState('');
  const [hatirlatmaVar, setHatirlatmaVar] = useState(false);
  const [hTitle, setHTitle] = useState('');
  const [hDue, setHDue] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);

  /** "3 gün sonra ara" en sık istenen; kutular hazır gelsin. */
  function gunSonra(gun: number) {
    const d = new Date(Date.now() + gun * 864e5);
    // datetime-local yerel saat ister; toISOString UTC verir (3 saat erken düşerdi).
    setHDue(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    setHatirlatmaVar(true);
    if (!hTitle) setHTitle(`${subject || 'Tekrar ara'}`);
  }

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    setHata(null); setBekliyor(true);
    try {
      await api(`/api/crm/cari/${customerId}/aktivite`, {
        method: 'POST',
        body: JSON.stringify({
          type, subject, body: body || undefined,
          contactId: contactId ? Number(contactId) : undefined,
          ...(hatirlatmaVar && hDue
            ? { hatirlatma: { title: hTitle || subject, dueAt: new Date(hDue).toISOString() } }
            : {}),
        }),
      });
      eklendi();
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Kaydedilemedi');
    } finally {
      setBekliyor(false);
    }
  }

  return (
    <div className="ccd-drawer-arka" onClick={kapat} role="presentation">
      <aside className="ccd-drawer" onClick={(e) => e.stopPropagation()}
             role="dialog" aria-modal="true" aria-label="Aktivite kaydı">
        <form className="ccd-stack" onSubmit={gonder}>
          <div className="ccd-row-between">
            <div>
              <div className="ccd-eyebrow">CRM</div>
              <h2 className="ccd-heading">Aktivite kaydı</h2>
            </div>
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>Kapat</button>
          </div>

          <div className="ccd-field">
            <label className="ccd-field-label">Tür</label>
            <div className="ccd-row" style={{ flexWrap: 'wrap' }}>
              {(['ARAMA', 'ZIYARET', 'MAIL', 'WHATSAPP', 'NUMUNE', 'NOT'] as const).map((t) => (
                <button key={t} type="button"
                        className={`ccd-btn ccd-btn-sm${type === t ? ' ccd-btn-primary' : ''}`}
                        onClick={() => setType(t)}>
                  {AKTIVITE_IKON[t]} {t.charAt(0) + t.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="ccd-field">
            <label className="ccd-field-label" htmlFor="akon">
              Konu<span className="ccd-required"> *</span>
            </label>
            <input id="akon" className="ccd-input" required autoFocus value={subject}
                   onChange={(e) => setSubject(e.target.value)}
                   placeholder="Fiyat sordu, numune istedi" />
          </div>

          <div className="ccd-field">
            <label className="ccd-field-label" htmlFor="adet2">Detay</label>
            <textarea id="adet2" className="ccd-input" rows={3} value={body}
                      onChange={(e) => setBody(e.target.value)} />
          </div>

          {kisiler.length > 0 && (
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="akisi">Kişi</label>
              <select id="akisi" className="ccd-select" value={contactId}
                      onChange={(e) => setContactId(e.target.value)}>
                <option value="">—</option>
                {kisiler.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </div>
          )}

          <div className="ccd-card ccd-stack-sm">
            <div className="ccd-fieldset-title">Hatırlatma</div>
            <div className="ccd-row" style={{ flexWrap: 'wrap' }}>
              {[1, 3, 7, 14].map((g) => (
                <button key={g} type="button" className="ccd-btn ccd-btn-sm" onClick={() => gunSonra(g)}>
                  {g} gün sonra
                </button>
              ))}
            </div>
            {hatirlatmaVar && (
              <>
                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor="hbas">Hatırlatma başlığı</label>
                  <input id="hbas" className="ccd-input" value={hTitle}
                         onChange={(e) => setHTitle(e.target.value)} />
                </div>
                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor="hvad">Vade</label>
                  <input id="hvad" className="ccd-input" type="datetime-local" value={hDue}
                         onChange={(e) => setHDue(e.target.value)} />
                  <div className="ccd-field-hint">Vadesi gelince Telegram'a düşer.</div>
                </div>
                <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm"
                        onClick={() => { setHatirlatmaVar(false); setHDue(''); }}>
                  Hatırlatmayı kaldır
                </button>
              </>
            )}
          </div>

          {hata && (
            <div className="ccd-banner ccd-banner-danger">
              <span className="ccd-banner-icon">!</span><span>{hata}</span>
            </div>
          )}

          <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor}>
            {bekliyor ? 'Kaydediliyor…' : 'Aktiviteyi kaydet'}
          </button>
        </form>
      </aside>
    </div>
  );
}
